/**
 * Planning API failures carry more than a message. A deterministic data-contract violation can
 * never succeed on retry, so the surface that renders it must be able to suppress the Retry
 * affordance instead of inviting the operator to press it forever.
 *
 * This lives outside `client.ts` on purpose: lane tests mock the client module wholesale, and an
 * `instanceof` check against a mocked export is never satisfied.
 */

export interface PlanningApiFailure {
  readonly message: string;
  readonly code: string | null;
  readonly retryable: boolean;
  /** Source column or field names implicated in the failure. Never source values. */
  readonly fields: readonly string[];
}

export function isSingleCompanyEntryError(error: unknown): boolean {
  return error instanceof PlanningApiError &&
    error.code === "AIRE_PLANNING_MULTI_COMPANY_SCENARIO_UNSUPPORTED";
}

export class PlanningApiError extends Error {
  override readonly name = "PlanningApiError";
  readonly code: string | null;
  readonly status: number;
  readonly retryable: boolean;
  readonly fields: readonly string[];

  constructor(
    message: string,
    detail: {
      readonly code?: string | null;
      readonly status: number;
      readonly retryable?: boolean;
      readonly fields?: readonly string[];
    },
  ) {
    super(message);
    this.code = detail.code ?? null;
    this.status = detail.status;
    this.retryable = detail.retryable ?? true;
    this.fields = detail.fields ?? [];
  }
}

/** Normalizes any thrown value into the shape a banner needs. Unknown causes stay retryable. */
export function planningApiFailure(error: unknown, fallbackMessage: string): PlanningApiFailure {
  if (error instanceof PlanningApiError) {
    return {
      message: error.message,
      code: error.code,
      retryable: error.retryable,
      fields: error.fields,
    };
  }
  return {
    message: error instanceof Error ? error.message : fallbackMessage,
    code: null,
    retryable: true,
    fields: [],
  };
}

/**
 * Builds a `PlanningApiError` from a non-OK JSON body. `error` is the AIRE error code, and
 * `context.invalid_fields` / `context.missing_fields` name source columns without their values.
 */
export function planningApiErrorFromPayload(payload: unknown, status: number): PlanningApiError {
  const body = typeof payload === "object" && payload !== null ? (payload as PlanningApiBody) : {};
  let message =
    typeof body.message === "string" && body.message.trim() !== ""
      ? body.message
      : `Request failed with ${status}`;
  if (body.error === "AIRE_PLANNING_CUBE_CARDINALITY_EXCEEDED") {
    const counts =
      typeof body.context === "object" && body.context !== null
        ? (body.context as Record<string, unknown>)
        : {};
    const count = (key: string): string =>
      typeof counts[key] === "number" && Number.isSafeInteger(counts[key]) && counts[key] >= 0
        ? String(counts[key])
        : "unknown";
    message +=
      counts.observed_source_rows === undefined
        ? ` Estimated cells: ${count("estimated_result_cells")} (limit ${count("max_result_cells")}). Remove the period grouping for a whole-window total, or filter to one company, division or product.`
        : ` Source rows: ${count("observed_source_rows")} (limit ${count("max_source_rows")}). Shorten the date window or filter to one company, division or product.`;
    message += " No partial totals were returned.";
  }
  return new PlanningApiError(message, {
    code: typeof body.error === "string" ? body.error : null,
    status,
    retryable: typeof body.retryable === "boolean" ? body.retryable : true,
    fields: contextFields(body.context),
  });
}

interface PlanningApiBody {
  readonly error?: unknown;
  readonly message?: unknown;
  readonly retryable?: unknown;
  readonly context?: unknown;
}

function contextFields(context: unknown): readonly string[] {
  if (typeof context !== "object" || context === null) return [];
  const record = context as Record<string, unknown>;
  return ["invalid_fields", "missing_fields"].flatMap((key) => {
    const value = record[key];
    return Array.isArray(value)
      ? value.filter((entry): entry is string => typeof entry === "string")
      : [];
  });
}
